/**
 * klubb-klubbpris — keeps the active club's klubbpris discount code applied
 * to the cart, so the klubbpris shown on PLP/PDP is what checkout charges.
 *
 * The code is slug-derived (`bsk` → `BSK-PRIS`) and created/managed by the
 * "Opprett klubbpris" admin action in the Klubb-app (docs/klubb-app.md). This module only
 * ever adds/removes codes from the exact set derivable from the clubs-data
 * slugs — shopper-entered codes (sponsorship klubbkoder etc.) are untouched.
 *
 * Mechanism: Cart AJAX API discount support — POST /cart/update.js with
 * `discount` = comma-separated desired code list (Shopify, May 2025).
 * Runs at boot, on club change (window.klubb 'change'), and on cart:update;
 * converges because a no-op diff never POSTs, and each distinct desired set
 * is attempted at most once per page load (a code Shopify won't persist —
 * e.g. not yet created in admin — can't turn into a hot loop).
 */

export function slugToCode(slug) {
  var base = String(slug).toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return base + '-PRIS';
}

// currentCodes: codes on the cart now. activeSlug: active club slug or null
// (null also when the active club has no discount_percent). allSlugs: every
// club slug from clubs-data — defines the managed set.
export function computeDesired(currentCodes, activeSlug, allSlugs) {
  var managed = new Set(allSlugs.map(slugToCode));
  var kept = currentCodes.filter(function (c) { return !managed.has(String(c).toUpperCase()); });
  if (activeSlug) kept.push(slugToCode(activeSlug));
  return kept;
}

// True when a code ending -PRIS is on the cart that none of `slugs` explains: a club's code this browser does not
// know yet (R2, D70: a page carries only its own club). Then the whole club directory is asked before pruning.
// Keep in step with klubb-cart-api.js.
export function hasUnknownClubCode(currentCodes, slugs) {
  var known = new Set(slugs.map(slugToCode));
  return currentCodes.some(function (c) { return /-PRIS$/i.test(String(c)) && !known.has(String(c).toUpperCase()); });
}

export function needsChange(currentCodes, desired) {
  var a = new Set(currentCodes.map(function (c) { return String(c).toUpperCase(); }));
  var b = new Set(desired.map(function (c) { return String(c).toUpperCase(); }));
  if (a.size !== b.size) return true;
  for (var c of a) if (!b.has(c)) return true;
  return false;
}

/* ============================================================
 * Browser wiring
 * ============================================================ */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Slugs of the clubs this browser knows (this page's, the member's own, the loaded directory). The managed set
  // that is pruned from the cart; the active club is always in it.
  function clubSlugs() {
    var k = window.klubb;
    var slugs = k && typeof k.knownClubs === 'function' ? k.knownClubs().map(function (c) { return c.slug; }) : [];
    if (k && k.activeClub && slugs.indexOf(k.activeClub.slug) === -1) slugs.push(k.activeClub.slug);
    return slugs;
  }

  var running = false;
  var timer = null;
  var lastAttempt = null;
  var queued = false;

  function sync() {
    if (running) { queued = true; return; }
    running = true;
    queued = false;
    var klubb = window.klubb;
    // The member's club first: a first visit without a stored copy has to look it up (it triggers 'change' too).
    Promise.resolve(klubb && klubb.ready ? klubb.ready(2500) : null).catch(function () { return null; })
      .then(function () {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count) return null; // empty cart: nothing to discount
        var active = klubb && klubb.activeClub && Number(klubb.activeClub.discount_percent) > 0
          ? klubb.activeClub.slug
          : null;
        var current = (cart.discount_codes || []).map(function (d) { return d && d.code; }).filter(Boolean);
        // Another club's leftover code must still go (two klubbpris codes could combine): if the cart holds a club
        // code this browser cannot account for, ask the directory for every club first.
        var known = hasUnknownClubCode(current, clubSlugs()) && klubb && klubb.directory
          ? klubb.directory().catch(function () { return []; })
          : Promise.resolve(null);
        return known.then(function () {
          var desired = computeDesired(current, active, clubSlugs());
          if (!needsChange(current, desired)) { lastAttempt = null; return null; }
          // One attempt per distinct set per page load: a code Shopify refuses to
          // persist (e.g. not yet created in admin) would otherwise hot-loop here.
          var key = desired.map(function (c) { return String(c).toUpperCase(); }).sort().join(',');
          if (key === lastAttempt) return null;
          lastAttempt = key;
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: desired.join(',') }),
          }).then(function (res) {
            if (!res.ok) return null;
            // The response body IS the updated cart — judge success from it
            // instead of trusting the POST blindly.
            return res.json().then(function (updated) {
              var now = (updated.discount_codes || []).map(function (d) { return d && d.code; }).filter(Boolean);
              if (!needsChange(current, now)) return null; // nothing actually changed: stay quiet
              document.dispatchEvent(new CustomEvent('cart:update'));
              // The full-page cart re-renders server-side (klubb-cart.js reloads on
              // qty changes for the same reason) — reload once so totals match. Only the cart PAGE:
              // the cart drawer (D66) has cart lines on every page and refreshes itself on cart:update.
              if (document.querySelector('[data-klubb-cart] [data-klubb-cart-line]')) window.location.reload();
              return null;
            }).catch(function () { return null; });
          });
        });
      })
      .catch(function () { /* leave the cart untouched on any failure */ })
      .finally(function () {
        running = false;
        if (queued) debouncedSync(); // an event arrived mid-flight — re-check with fresh state
      });
  }

  function debouncedSync() {
    clearTimeout(timer);
    timer = setTimeout(sync, 200);
  }

  if (window.klubb && typeof window.klubb.on === 'function') {
    window.klubb.on('change', debouncedSync);
  }
  document.addEventListener('cart:update', debouncedSync);
  debouncedSync(); // boot: converge whatever state the cart is in
})();
